import { useEffect, useLayoutEffect, useRef } from 'react'
import type { FaceSignals, EyeContact } from 'eyecontact'
import { cn } from '../lib/cn'
import { Meter } from './ui'

/**
 * Mirror of what the detector sees, with the mesh drawn on top.
 *
 * Two reasons this exists beyond looking good: it tells you instantly whether
 * the camera is framed badly or the room is too dark, and it makes threshold
 * tuning something you can do by eye instead of by trial and error.
 */
export function CameraPreview({
  gate,
  signals,
  attentive,
  running,
  showMesh,
  gazeTolerance,
}: {
  gate: EyeContact | null
  signals: FaceSignals
  attentive: boolean
  running: boolean
  showMesh: boolean
  gazeTolerance: number
}) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null)
  const rafRef = useRef<number | null>(null)
  // Read live values inside the draw loop instead of restarting it every
  // frame. Assigned in a layout effect rather than during render: a render
  // that concurrent React discards must not leave uncommitted values here.
  const latest = useRef({ signals, attentive, showMesh })
  useLayoutEffect(() => {
    latest.current = { signals, attentive, showMesh }
  })

  useEffect(() => {
    if (!running || !gate) return
    const canvas = canvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext('2d')
    if (!ctx) return

    let alive = true

    const draw = () => {
      if (!alive) return
      const video = gate.videoElement
      const { signals: s, attentive: att, showMesh: mesh } = latest.current

      if (video && video.videoWidth > 0) {
        if (canvas.width !== video.videoWidth || canvas.height !== video.videoHeight) {
          canvas.width = video.videoWidth
          canvas.height = video.videoHeight
        }

        // Mirror it. An un-mirrored self-view is deeply disorienting.
        ctx.save()
        ctx.translate(canvas.width, 0)
        ctx.scale(-1, 1)
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height)
        ctx.restore()

        if (mesh && s.landmarks?.length) {
          ctx.save()
          ctx.translate(canvas.width, 0)
          ctx.scale(-1, 1)
          ctx.fillStyle = att ? 'rgba(20, 224, 122, 0.55)' : 'rgba(245, 181, 68, 0.55)'
          // 478 points at full density is mush; every 4th reads as a mesh.
          for (let i = 0; i < s.landmarks.length; i += 4) {
            const p = s.landmarks[i]
            if (!p) continue
            ctx.fillRect(p.x * canvas.width - 1, p.y * canvas.height - 1, 2, 2)
          }
          ctx.restore()
        }

        // Gaze reticle: where the eyes are pointing, against the tolerance ring.
        if (s.faceFound) {
          const cx = canvas.width / 2
          const cy = canvas.height / 2
          const radius = Math.min(canvas.width, canvas.height) * 0.34

          ctx.strokeStyle = 'rgba(255,255,255,0.16)'
          ctx.lineWidth = 1
          ctx.beginPath()
          ctx.arc(cx, cy, radius * gazeTolerance, 0, Math.PI * 2)
          ctx.stroke()

          // Negated on x because the canvas is mirrored.
          const gx = cx - s.gazeX * radius
          const gy = cy - s.gazeY * radius
          ctx.fillStyle = att ? '#14e07a' : '#f5b544'
          ctx.beginPath()
          ctx.arc(gx, gy, 4, 0, Math.PI * 2)
          ctx.fill()
          ctx.strokeStyle = 'rgba(0,0,0,0.5)'
          ctx.stroke()
        }
      }
      rafRef.current = requestAnimationFrame(draw)
    }

    rafRef.current = requestAnimationFrame(draw)
    return () => {
      alive = false
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current)
      // Wipe the last frame. The "Camera off" overlay is transparent, so
      // otherwise the user's face stays on screen after they hit Stop, which
      // is a poor look on a page whose whole pitch is that nothing leaves the
      // machine.
      ctx.clearRect(0, 0, canvas.width, canvas.height)
    }
  }, [gate, running, gazeTolerance])

  return (
    <div className="space-y-3 p-3.5">
      <div
        className={cn(
          'relative aspect-[4/3] w-full overflow-hidden rounded-md border bg-black transition-colors',
          running ? (attentive ? 'border-accent/40' : 'border-warn/40') : 'border-line',
        )}
      >
        <canvas ref={canvasRef} className="h-full w-full object-cover" />
        {!running && (
          <div className="absolute inset-0 grid place-items-center bg-black text-2xs text-faint">
            Camera off
          </div>
        )}
        {running && !signals.faceFound && (
          <div className="absolute inset-x-0 bottom-0 bg-black/70 py-1 text-center text-2xs text-warn">
            No face detected
          </div>
        )}
      </div>

      <div className="grid grid-cols-2 gap-x-4 gap-y-2">
        <Meter label="yaw" value={signals.yawDeg} max={60} signed />
        <Meter label="pitch" value={signals.pitchDeg} max={45} signed />
        <Meter label="gaze x" value={signals.gazeX} max={1} signed />
        <Meter label="gaze y" value={signals.gazeY} max={1} signed />
        <Meter label="blink" value={signals.blink} max={1} danger={signals.blink > 0.55} />
        <Meter label="drift" value={Math.hypot(signals.gazeX, signals.gazeY)} max={1} danger={Math.hypot(signals.gazeX, signals.gazeY) > gazeTolerance} />
      </div>
    </div>
  )
}
